export const TODAY_VIEW_STORAGE_KEY = 'coachapp:today-view';

export type TodayView = 'day' | 'week';

export type DayBooking = {
  id: string;
  status: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | string;
  startsAt: string;
  durationMinutes: number;
};

const pad = (value: number) => String(value).padStart(2, '0');

export function formatIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseIsoDate(isoDate: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

export function parseDateParam(search: string, now: Date): string {
  const raw = new URLSearchParams(search).get('date');
  if (raw && parseIsoDate(raw)) return raw;
  return formatIsoDate(now);
}

export function addDays(isoDate: string, days: number): string {
  const date = parseIsoDate(isoDate) ?? new Date();
  date.setDate(date.getDate() + days);
  return formatIsoDate(date);
}

export function relativeDay(
  isoDate: string,
  now: Date,
): 'today' | 'tomorrow' | 'yesterday' | 'other' {
  const today = formatIsoDate(now);
  if (isoDate === today) return 'today';
  if (isoDate === addDays(today, 1)) return 'tomorrow';
  if (isoDate === addDays(today, -1)) return 'yesterday';
  return 'other';
}

export function getMonday(date: Date): Date {
  const monday = new Date(date);
  const weekday = monday.getDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  monday.setDate(monday.getDate() + mondayOffset);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

export function shiftWeek(isoDate: string, direction: 1 | -1): string {
  const date = parseIsoDate(isoDate) ?? new Date();
  const monday = getMonday(date);
  if (direction > 0) {
    monday.setDate(monday.getDate() + 7);
    return formatIsoDate(monday);
  }
  monday.setDate(monday.getDate() - 1);
  return formatIsoDate(monday);
}

export function weekDates(isoDate: string): string[] {
  const date = parseIsoDate(isoDate) ?? new Date();
  const monday = getMonday(date);
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + index);
    return formatIsoDate(day);
  });
}

function endMs(item: DayBooking): number {
  return new Date(item.startsAt).getTime() + item.durationMinutes * 60_000;
}

function isActive(status: string): boolean {
  return status === 'PENDING' || status === 'CONFIRMED';
}

export function splitDayBookings<T extends DayBooking>(items: T[], isoDate: string, now: Date) {
  const todayIso = formatIsoDate(now);
  const day = items
    .filter((item) => formatIsoDate(new Date(item.startsAt)) === isoDate)
    .slice()
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  const done = day.filter((item) => item.status === 'COMPLETED' || item.status === 'CANCELLED');
  const active = day.filter((item) => isActive(item.status));

  let hero: T | null = null;
  if (isoDate === todayIso) {
    const nowMs = now.getTime();
    const inProgress = active.filter(
      (item) => new Date(item.startsAt).getTime() <= nowMs && endMs(item) > nowMs,
    );
    if (inProgress.length > 0) {
      hero = inProgress[inProgress.length - 1];
    } else {
      hero = active.find((item) => new Date(item.startsAt).getTime() > nowMs) ?? null;
    }
  } else {
    hero = active[0] ?? null;
  }

  const later = active.filter((item) => item.id !== hero?.id);
  return { hero, later, done };
}

export function minutesUntilLabel(isoDate: string, startsAt: string, now: Date): number | null {
  if (isoDate !== formatIsoDate(now)) return null;
  const start = new Date(startsAt).getTime();
  if (start <= now.getTime()) return null;
  return Math.round((start - now.getTime()) / 60_000);
}

export function datesWithBookings(items: Array<{ startsAt: string }>): Set<string> {
  return new Set(items.map((item) => formatIsoDate(new Date(item.startsAt))));
}

export function readTodayView(storage: { getItem(key: string): string | null }): TodayView {
  return storage.getItem(TODAY_VIEW_STORAGE_KEY) === 'week' ? 'week' : 'day';
}
